import { useState, useEffect, useCallback } from 'react';
import { getUsersByRole } from '../api';
import { useAuth } from '@/hooks/useAuth';
import {
  getQueueSettings, updateQueueSettings,
  getDoctorAvailability, createDoctorAvailability, deleteDoctorAvailability,
  addDoctorBreak, deleteDoctorBreak,
  getAvailabilityExceptions, reportUnavailability,
  cancelAppointmentSlot,
} from '../api';
import type { Doctor, QueueSettings, DoctorAvailabilityDay, AvailabilityException, ReportUnavailabilityResult } from '../types';

const DEFAULT_LOCK_MINUTES = 2880; // 48h

function minLockDate(lockMinutes: number) {
  const d = new Date(Date.now() + lockMinutes * 60 * 1000);
  return d.toISOString().slice(0, 10);
}
function defaultCreateDate(lockMinutes: number) {
  const d = new Date(Date.now() + (lockMinutes + 1440) * 60 * 1000); // +24h buffer past the lock boundary
  return d.toISOString().slice(0, 10);
}
// "48h", "45m", "2h 30m" — how staff think about the lock window, rather than
// surfacing raw minutes for large values.
function fmtLockDuration(minutes: number) {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}
function fmtDate(d: string) { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtTime(t: string) { return (t || '').slice(0, 5); }

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  draft: { bg: '#F3F4F6', color: '#4B5563', label: 'Draft' },
  published: { bg: '#D1FAE5', color: '#065F46', label: 'Published' },
  locked: { bg: '#FEF3C7', color: '#92400E', label: 'Locked' },
  completed: { bg: '#E5E7EB', color: '#374151', label: 'Completed' },
};

export default function DoctorAvailabilityTab() {
  const { branchVersion } = useAuth();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorId, setDoctorId] = useState('');
  const [days, setDays] = useState<DoctorAvailabilityDay[] | null>(null);
  const [error, setError] = useState('');

  const [settings, setSettings] = useState<QueueSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ date: defaultCreateDate(DEFAULT_LOCK_MINUTES), start_time: '09:00', end_time: '13:00', slot_duration_minutes: '15' });
  const [breakDraft, setBreakDraft] = useState<Record<number, { start_time: string; end_time: string; label: string } | null>>({});

  const [reporting, setReporting] = useState(false);
  const [excForm, setExcForm] = useState({ date: new Date().toISOString().slice(0, 10), exception_start: '', exception_end: '', reason: '' });
  const [excResult, setExcResult] = useState<ReportUnavailabilityResult | null>(null);
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([]);

  useEffect(() => { getUsersByRole('Doctor').then((rows) => { setDoctors(rows); if (rows.length) setDoctorId(String(rows[0]!.id)); }).catch(() => {}); }, []);
  useEffect(() => { getQueueSettings().then(setSettings).catch((e: any) => setError(e.message)); }, []);

  const loadDays = useCallback(() => {
    if (!doctorId) return;
    getDoctorAvailability({ doctor_id: doctorId, from: new Date().toISOString().slice(0, 10) }).then(setDays).catch((e: any) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, branchVersion]);
  const loadExceptions = useCallback(() => {
    if (!doctorId) return;
    getAvailabilityExceptions({ doctor_id: doctorId }).then(setExceptions).catch(() => {});
  }, [doctorId]);
  useEffect(() => { loadDays(); loadExceptions(); }, [loadDays, loadExceptions]);

  const lockMinutes = settings?.availability_lock_minutes ? parseInt(String(settings.availability_lock_minutes), 10) : DEFAULT_LOCK_MINUTES;

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true); setError('');
    try {
      const saved = await updateQueueSettings({
        default_slot_minutes: Number(settings.default_slot_minutes),
        delay_notify_threshold_minutes: Number(settings.delay_notify_threshold_minutes),
        booking_cutoff_minutes: Number(settings.booking_cutoff_minutes),
        emergency_handling_mode: settings.emergency_handling_mode,
        availability_lock_minutes: Number(settings.availability_lock_minutes),
        late_checkin_grace_minutes: Number(settings.late_checkin_grace_minutes),
        wait_time_breakdown_window: settings.wait_time_breakdown_window,
        timezone: settings.timezone,
      });
      setSettings(saved);
    } catch (e: any) { setError(e.message); }
    setSavingSettings(false);
  };

  const startCreating = () => {
    setCreating(true);
    setForm((f) => ({ ...f, date: defaultCreateDate(lockMinutes) }));
  };

  const submitDay = async () => {
    setError('');
    try {
      await createDoctorAvailability({ doctor_id: doctorId, ...form, slot_duration_minutes: parseInt(form.slot_duration_minutes, 10) });
      setCreating(false);
      setForm({ date: defaultCreateDate(lockMinutes), start_time: '09:00', end_time: '13:00', slot_duration_minutes: String(settings?.default_slot_minutes || 15) });
      await loadDays();
    } catch (e: any) { setError(e.message); }
  };

  const removeDay = async (id: number) => {
    try { await deleteDoctorAvailability(id); await loadDays(); } catch (e: any) { setError(e.message); }
  };

  const submitBreak = async (availId: number) => {
    const draft = breakDraft[availId];
    if (!draft?.start_time || !draft?.end_time) return;
    try {
      await addDoctorBreak(availId, draft);
      setBreakDraft((d) => ({ ...d, [availId]: null }));
      await loadDays();
    } catch (e: any) { setError(e.message); }
  };

  const removeBreak = async (availId: number, breakId: number) => {
    try { await deleteDoctorBreak(availId, breakId); await loadDays(); } catch (e: any) { setError(e.message); }
  };

  const submitException = async () => {
    if (!excForm.exception_start || !excForm.exception_end || !excForm.reason.trim()) { setError('Start, end and reason are required'); return; }
    setError(''); setExcResult(null);
    try {
      const result = await reportUnavailability({ doctor_id: doctorId, ...excForm });
      setExcResult(result);
      setReporting(false);
      setExcForm({ date: new Date().toISOString().slice(0, 10), exception_start: '', exception_end: '', reason: '' });
      await Promise.all([loadDays(), loadExceptions()]);
    } catch (e: any) { setError(e.message); }
  };

  const cancelAffectedSlot = async (slotId: number) => {
    try { await cancelAppointmentSlot(slotId, 'Cancelled during emergency triage'); await loadExceptions(); } catch (e: any) { setError(e.message); }
  };

  const selectedDoctorDefault = doctors.find((d) => String(d.id) === doctorId);

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
        Configure doctor availability at least {fmtLockDuration(lockMinutes)} in advance — bookable slots are generated automatically once a day locks.
      </div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      {settings && (
        <div className="card" style={{ padding: 14, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 10 }}>⚙ Queue settings</div>
          <div className="grid2" style={{ marginBottom: 10 }}>
            <div>
              <label className="lbl">Default slot duration (min)</label>
              <input type="number" value={settings.default_slot_minutes} onChange={(e) => setSettings((s) => s && ({ ...s, default_slot_minutes: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="lbl">Delay notify threshold (min)</label>
              <input type="number" value={settings.delay_notify_threshold_minutes} onChange={(e) => setSettings((s) => s && ({ ...s, delay_notify_threshold_minutes: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="lbl">Booking cutoff (min before slot)</label>
              <input type="number" value={settings.booking_cutoff_minutes} onChange={(e) => setSettings((s) => s && ({ ...s, booking_cutoff_minutes: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="lbl">Availability lock (in advance)</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="number" min="0" style={{ width: 70 }} title="Hours"
                  value={Math.floor(settings.availability_lock_minutes / 60)}
                  onChange={(e) => setSettings((s) => s && ({ ...s, availability_lock_minutes: Math.max(1, (Number(e.target.value) || 0) * 60 + (s.availability_lock_minutes % 60)) }))}
                />
                <span style={{ fontSize: 12, color: '#6B7280' }}>h</span>
                <input
                  type="number" min="0" max="59" step="15" style={{ width: 70 }} title="Minutes"
                  value={settings.availability_lock_minutes % 60}
                  onChange={(e) => setSettings((s) => s && ({ ...s, availability_lock_minutes: Math.max(1, Math.floor(s.availability_lock_minutes / 60) * 60 + (Number(e.target.value) || 0)) }))}
                />
                <span style={{ fontSize: 12, color: '#6B7280' }}>m</span>
              </div>
              <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 4 }}>Slots lock (become bookable) when current time gets within this window of the slot start time.</div>
            </div>
            <div>
              <label className="lbl">Late check-in grace (min)</label>
              <input type="number" min="0" value={settings.late_checkin_grace_minutes} onChange={(e) => setSettings((s) => s && ({ ...s, late_checkin_grace_minutes: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="lbl">Timezone</label>
              <input
                type="text" value={settings.timezone || ''} placeholder="Asia/Kolkata"
                onChange={(e) => setSettings((s) => s && ({ ...s, timezone: e.target.value }))}
              />
              <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 4 }}>
                IANA zone (e.g. Asia/Kolkata) used to interpret every date + start time above (availability lock, slot expiry, booking cutoff) as real wall-clock time. Only change this if the hospital is genuinely in a different timezone.
              </div>
            </div>
            <div>
              <label className="lbl">Emergency handling mode</label>
              <select value={settings.emergency_handling_mode} onChange={(e) => setSettings((s) => s && ({ ...s, emergency_handling_mode: e.target.value as QueueSettings['emergency_handling_mode'] }))}>
                <option value="manual">Manual (staff triages every affected patient)</option>
                <option value="automatic">Automatic (same-specialty reassignment where possible)</option>
              </select>
            </div>
            <div>
              <label className="lbl">Dashboard wait-time breakdown window</label>
              <select value={settings.wait_time_breakdown_window} onChange={(e) => setSettings((s) => s && ({ ...s, wait_time_breakdown_window: e.target.value as QueueSettings['wait_time_breakdown_window'] }))}>
                <option value="today">Today</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="6m">Last 6 months</option>
              </select>
              <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 4 }}>
                How much history the Dashboard's "Patient wait time breakdown" card averages over. A shorter window (e.g. Today) shows real, current wait times more clearly; a longer one smooths out day-to-day noise but dilutes a single bad day.
              </div>
            </div>
          </div>
          <button className="btn-primary" onClick={saveSettings} disabled={savingSettings}>{savingSettings ? 'Saving…' : 'Save settings'}</button>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label className="lbl">Doctor</label>
        <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} style={{ maxWidth: 320 }}>
          {doctors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 20, borderColor: '#FCA5A5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: '#991B1B' }}>🚨 Report unavailability</div>
          {!reporting && <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setReporting(true)}>Report now</button>}
        </div>
        {reporting && (
          <div style={{ marginTop: 10 }}>
            <div className="grid2" style={{ marginBottom: 10 }}>
              <div><label className="lbl">Date</label><input type="date" value={excForm.date} onChange={(e) => setExcForm((f) => ({ ...f, date: e.target.value }))} /></div>
              <div><label className="lbl">From</label><input type="time" value={excForm.exception_start} onChange={(e) => setExcForm((f) => ({ ...f, exception_start: e.target.value }))} /></div>
              <div><label className="lbl">To</label><input type="time" value={excForm.exception_end} onChange={(e) => setExcForm((f) => ({ ...f, exception_end: e.target.value }))} /></div>
              <div><label className="lbl">Reason (required)</label><input value={excForm.reason} onChange={(e) => setExcForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Called in sick" /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={submitException}>Submit</button>
              <button className="btn-ghost" onClick={() => setReporting(false)}>Cancel</button>
            </div>
          </div>
        )}
        {excResult && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 'var(--radius)', fontSize: 12.5, color: '#065F46' }}>
            {excResult.mode === 'automatic' && excResult.auto_reassigned.length > 0
              ? `Automatically reassigned ${excResult.auto_reassigned.length} patient(s) to a same-specialty doctor.`
              : 'Reported. Any affected patients now show below for manual triage.'}
          </div>
        )}
      </div>

      {exceptions.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>Recent unavailability reports</div>
          {exceptions.map((ex) => (
            <div key={ex.id} className="card" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 12.5, marginBottom: 6 }}>
                <strong>{fmtDate(ex.date)}</strong>, {fmtTime(ex.exception_start)}–{fmtTime(ex.exception_end)} — {ex.reason}
              </div>
              {ex.affected.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>No affected patients.</div>
              ) : ex.affected.map((a, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#FEF3C7', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                  <span>{a.patient_name} ({a.patient_mobile}) — {a.type === 'slot' ? `booked ${fmtTime(a.start_time || '')}` : 'already checked in (Queue)'}</span>
                  {a.type === 'slot' ? (
                    <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => cancelAffectedSlot(a.id)}>Cancel &amp; notify</button>
                  ) : (
                    <span style={{ fontSize: 11, color: '#92400E' }}>Reassign doctor from the Queue screen</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>Availability days</div>
        {!creating && <button className="btn-primary" style={{ fontSize: 12 }} onClick={startCreating}>+ New day</button>}
      </div>

      {creating && (
        <div className="card" style={{ padding: 14, marginBottom: 12 }}>
          <div className="grid2" style={{ marginBottom: 10 }}>
            <div><label className="lbl">Date (min. {fmtLockDuration(lockMinutes)} ahead)</label><input type="date" min={minLockDate(lockMinutes)} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="lbl">Slot duration (min)</label><input type="number" value={form.slot_duration_minutes} onChange={(e) => setForm((f) => ({ ...f, slot_duration_minutes: e.target.value }))} /></div>
            <div><label className="lbl">Start time</label><input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} /></div>
            <div><label className="lbl">End time</label><input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={submitDay}>Create</button>
            <button className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {days === null ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading…</div>
      ) : days.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>No availability configured for {selectedDoctorDefault?.full_name || 'this doctor'} yet.</div>
      ) : days.map((day) => {
        const st = STATUS_STYLE[day.status] || STATUS_STYLE.draft!;
        const draft = breakDraft[day.id] || { start_time: '', end_time: '', label: '' };
        return (
          <div key={day.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <strong style={{ fontSize: 14 }}>{fmtDate(day.date)}</strong>{' '}
                <span style={{ fontSize: 13, color: '#6B7280' }}>{fmtTime(day.start_time)}–{fmtTime(day.end_time)} · {day.slot_duration_minutes} min slots</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                {(day.status === 'draft' || day.status === 'published') && (
                  <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeDay(day.id)}>Delete</button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              {(day.breaks || []).map((b) => (
                <span key={b.id} style={{ padding: '4px 10px', borderRadius: 14, fontSize: 11.5, background: '#F3F4F6', color: '#4B5563', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {b.label ? `${b.label}: ` : ''}{fmtTime(b.start_time)}–{fmtTime(b.end_time)}
                  {(day.status === 'draft' || day.status === 'published') && (
                    <span style={{ cursor: 'pointer', color: '#991B1B' }} onClick={() => removeBreak(day.id, b.id)}>✕</span>
                  )}
                </span>
              ))}
            </div>

            {(day.status === 'draft' || day.status === 'published') && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="time" placeholder="Start" value={draft.start_time} onChange={(e) => setBreakDraft((d) => ({ ...d, [day.id]: { ...draft, start_time: e.target.value } }))} style={{ width: 100, fontSize: 12 }} />
                <input type="time" placeholder="End" value={draft.end_time} onChange={(e) => setBreakDraft((d) => ({ ...d, [day.id]: { ...draft, end_time: e.target.value } }))} style={{ width: 100, fontSize: 12 }} />
                <input placeholder="Label (optional)" value={draft.label} onChange={(e) => setBreakDraft((d) => ({ ...d, [day.id]: { ...draft, label: e.target.value } }))} style={{ width: 130, fontSize: 12 }} />
                <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => submitBreak(day.id)}>+ Add break</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
